"use client"

import { useHasConsent } from "@webscraping/consent"
import { usePostHogConsentGate } from "@webscraping/consent/posthog"
import posthog from "posthog-js"
import { PostHogProvider as PHProvider } from "posthog-js/react"
import { useEffect, useMemo, useState } from "react"
import { env } from "~/env"
import { clearChunkReloadGuard } from "~/lib/chunk-load-error"
import { POSTHOG_DISTINCT_ID_COOKIE } from "~/lib/posthog/constants"

function readCookie(name: string): string | undefined {
  if (typeof document === "undefined") return undefined

  const prefix = `${name}=`
  const encodedValue = document.cookie
    .split(";")
    .map(cookie => cookie.trim())
    .find(cookie => cookie.startsWith(prefix))
    ?.slice(prefix.length)

  if (!encodedValue) return undefined

  try {
    return decodeURIComponent(encodedValue)
  } catch {
    return encodedValue
  }
}

function readPosthogDistinctId(): string | undefined {
  return readCookie(POSTHOG_DISTINCT_ID_COOKIE)
}

export function PostHogProvider({ children }: Readonly<{ children: React.ReactNode }>) {
  const measurementAllowed = useHasConsent("measurement")
  const [distinctId, setDistinctId] = useState(readPosthogDistinctId)

  // A successful client mount means the current bundle loaded fine, so clear
  // the one-time chunk-reload guard - the next redeploy can self-heal again.
  useEffect(() => {
    clearChunkReloadGuard()
  }, [])

  useEffect(() => {
    if (!measurementAllowed) {
      setDistinctId(undefined)
      return
    }

    const syncDistinctId = () => {
      const currentDistinctId = readPosthogDistinctId()
      if (currentDistinctId) {
        setDistinctId(previous => (previous === currentDistinctId ? previous : currentDistinctId))
      }
    }

    syncDistinctId()
    if (distinctId) return

    const retry = globalThis.setInterval(syncDistinctId, 1000)
    globalThis.addEventListener("focus", syncDistinctId)
    document.addEventListener("visibilitychange", syncDistinctId)

    return () => {
      globalThis.clearInterval(retry)
      globalThis.removeEventListener("focus", syncDistinctId)
      document.removeEventListener("visibilitychange", syncDistinctId)
    }
  }, [distinctId, measurementAllowed])

  const options = useMemo(
    () => ({
      api_host: env.NEXT_PUBLIC_POSTHOG_HOST,
      defaults: "2026-01-30" as const,
      bootstrap: distinctId ? { distinctID: distinctId } : undefined,
    }),
    [distinctId],
  )

  usePostHogConsentGate({ token: env.NEXT_PUBLIC_POSTHOG_PROJECT_TOKEN, options })

  useEffect(() => {
    if (!measurementAllowed || !distinctId || !posthog.__loaded) return
    if (!posthog.has_opted_in_capturing()) return
    if (posthog.get_distinct_id() === distinctId) return

    posthog.identify(distinctId)
  }, [distinctId, measurementAllowed])

  return <PHProvider client={posthog}>{children}</PHProvider>
}
